import { Injectable } from '@angular/core';
import * as signalR from '@microsoft/signalr';
import { BehaviorSubject, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { consoleLog } from '../app-utils';

@Injectable({
  providedIn: 'root'
})
export class SignalrService {
  connection: signalR.HubConnection;
  connected$ = new BehaviorSubject(false);
  reconnecting$ = new BehaviorSubject(false);
  private signalRUrl: string;
  private handlers: { [key: string]: { [key: string]: any } } = {};

  constructor() {
    this.signalRUrl = `${this.getSignalROrigin()}/websockets/notification`;
    this.start();
  }

  start(isReconnect = false) {
    consoleLog(`Connecting to SignalR at: ${this.signalRUrl}`);

    this.connection = new signalR.HubConnectionBuilder().withUrl(this.signalRUrl).build();

    this.connection
      .start()
      .then(() => {
        this.connected$.next(true);
        this.reconnecting$.next(false);

        if (isReconnect) {
          this.reRegisterAllHandlers();
        }

        this.reconnect();
      })
      .catch(() => {
        this.connected$.next(false);
        this.reconnecting$.next(true);
        setTimeout(() => {
          this.start(isReconnect);
        }, 10000);
      });
  }

  private getSignalROrigin() {
    //On a server the SignalR is deployed under the virtual directory "signalr", on localhost isn't
    if (environment.origin.includes('localhost')) {
      return 'https://localhost:44354';
    } else {
      return environment.protocol + environment.origin + '/signalr';
    }
  }

  reconnect() {
    this.connection.onclose(() => {
      this.connected$.next(false);
      this.reconnecting$.next(true);
      this.start(true);
    });
  }

  disconnect() {
    this.connection.stop().then(() => {
      consoleLog('Disconnecting from SignalR...');
      this.connected$.next(false);
      this.reconnecting$.next(false);
    });
  }

  /**
   * Invoke method on the server
   * Returns promise which resolves another Promise with server response
   * @param methodName
   * @param args
   */
  invoke(methodName: string, ...args): Promise<Promise<any>> {
    const unsubscribe$ = new Subject<void>();
    const alreadyConnected = this.connected$.getValue();

    // make sure that connection is available when invoking method
    const invokePromise = new Promise<Promise<any>>((resolve) => {
      if (alreadyConnected) {
        resolve(this.connection.invoke(methodName, ...args));
      } else {
        this.connected$.pipe(takeUntil(unsubscribe$)).subscribe((connected) => {
          if (connected) {
            unsubscribe$.next();
            unsubscribe$.complete();
            resolve(this.connection.invoke(methodName, ...args));
          }
        });
      }
    });

    return invokePromise;
  }

  /**
   * This function reregisters all existing handlers if we're reconnecting to SignalR service.
   * Otherwise handlers would become invalid.
   */
  reRegisterAllHandlers() {
    if (this.handlers) {
      Object.keys(this.handlers).forEach((componentName) => {
        const component = this.handlers[componentName];

        Object.keys(component).forEach((methodName) => {
          const method = component[methodName];
          this.registerHandler(componentName, methodName, method);
        });
      });
    }
  }

  /**
   * This function will register new handler and stores its reference to handlers object for easier access and removal of handler.
   * If handler is already registered, it will remove this handler and registers new.
   *
   * @param {String} componentName - NOTE: In case of a class you can use this.constructor.name. Name of the component where handler is registered
   * @param {String} methodName - Name of the method that is invoked
   * @param {Function} callback - The handler that will be raised when the hub method is invoked.
   */
  registerHandler(componentName: string, methodName: string, callback: (...args: any[]) => void) {
    const componentHandlers = this.handlers[componentName];
    const currentSubscription = componentHandlers && componentHandlers[methodName];

    currentSubscription && this.connection.off(methodName);

    this.handlers[componentName] = { ...componentHandlers };
    this.handlers[componentName][methodName] = callback;
    this.connection.on(methodName, callback);
  }

  /**
   * This function will remove handler and also remove its reference from handlers object.
   * If no `methodName` is provided, all handlers for provided component name are removed
   *
   * @param {String} fromComponent - Name of the component to remove handlers from
   * @param {String} fromMethod - Name of the method to remove handlers for - Pass `fromMethod` if you want to remove specific handler; Otherwise all handlers are removed
   */
  removeHandler(fromComponent: string, fromMethod?: string) {
    const componentHandlers = this.handlers[fromComponent];
    const hasHandlers = componentHandlers && Object.keys(componentHandlers);

    if (!hasHandlers) {
      return;
    }

    // remove all form component
    if (!fromMethod) {
      for (const methodName in this.handlers[fromComponent]) {
        const handler = this.handlers[fromComponent][methodName];
        handler && this.connection.off(methodName);
      }

      delete this.handlers[fromComponent];
    } else {
      // remove single
      const handler = componentHandlers[fromMethod];

      handler && this.connection.off(fromMethod);

      delete componentHandlers[fromMethod];
    }
  }
}
